import React, { useState } from 'react';
import {
  Timeline,
  Card,
  Typography,
  Tag,
  Collapse,
  List,
  Avatar,
  Divider,
  Button,
  Space,
  Row,
  Col,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LockOutlined,
  FileTextOutlined,
  TeamOutlined,
  CarryOutOutlined,
  PlayCircleOutlined,
  CheckOutlined,
  WarningOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { defaultColors } from '@/themes/antd-theme';
import './WorkflowTimelineAntd.css';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * WorkflowTimelineAntd Component
 * Pharma-compliant change control workflow using Ant Design
 */
const WorkflowTimelineAntd = ({
  workflow,
  currentStage = 1,
  workflowData = null,
  onStageAction,
  onApprovalClick,
  isDark = false
}) => {
  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[700] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  const [activeKeys, setActiveKeys] = useState([currentStage.toString()]);

  const getStageStatus = (stageNumber) => {
    if (stageNumber < currentStage) return 'completed';
    if (stageNumber === currentStage) return 'active';
    return 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'active':
        return 'processing';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ fontSize: '24px' }} />;
      case 'active':
        return <ClockCircleOutlined style={{ fontSize: '24px' }} />;
      case 'pending':
        return <ClockCircleOutlined style={{ fontSize: '24px', color: '#d9d9d9' }} />;
      default:
        return null;
    }
  };

  const renderStakeholders = (stakeholders) => {
    return (
      <div className="stakeholders-section">
        <Title level={5} style={{ color: '#1890ff', marginBottom: 12 }}>
          <TeamOutlined /> Stakeholders & Responsibilities
        </Title>
        <List
          dataSource={stakeholders}
          renderItem={(stakeholder, index) => (
            <List.Item style={{ padding: '8px 0' }}>
              <List.Item.Meta
                avatar={
                  <Avatar style={{ backgroundColor: isDark ? defaultColors.palette.gray[600] : '#e6f7ff', color: '#1890ff' }}>
                    {stakeholder.role.charAt(0)}
                  </Avatar>
                }
                title={<Text strong style={{ color: textColor }}>{stakeholder.role}</Text>}
                description={
                  <>
                    <Text style={{ display: 'block', fontSize: '12px', color: secondaryTextColor }}>
                      {stakeholder.responsibility}
                    </Text>
                    <Text style={{ display: 'block', fontSize: '12px', fontStyle: 'italic', color: '#1890ff' }}>
                      → {stakeholder.action}
                    </Text>
                  </>
                }
              />
            </List.Item>
          )}
        />
      </div>
    );
  };

  const renderKeyActivities = (activities) => {
    return (
      <div className="activities-section">
        <Title level={5} style={{ color: '#52c41a', marginBottom: 12 }}>
          <CarryOutOutlined /> Key Activities
        </Title>
        <List
          dataSource={activities}
          renderItem={(activity, index) => (
            <List.Item style={{ padding: '8px 0', paddingLeft: 0 }}>
              <Space>
                <Tag color="blue" style={{ minWidth: 32, textAlign: 'center' }}>
                  {index + 1}
                </Tag>
                <Text style={{ color: textColor }}>{activity}</Text>
              </Space>
            </List.Item>
          )}
        />
      </div>
    );
  };

  const renderOutput = (output, status) => {
    const outputBgColor = isDark
      ? defaultColors.palette.gray[600]
      : status === 'completed' ? '#f6ffed' : '#fff7e6';
    const outputBorderColor = status === 'completed' ? '#52c41a' : '#faad14';

    return (
      <Card
        size="small"
        style={{
          marginTop: 16,
          backgroundColor: outputBgColor,
          borderColor: outputBorderColor
        }}
      >
        <Title level={5} style={{ color: status === 'completed' ? '#389e0d' : '#d46b08', marginBottom: 12 }}>
          <FileTextOutlined /> Output & Deliverables
        </Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ color: textColor }}>Document: </Text>
            <Text style={{ color: textColor }}>{output.document}</Text>
          </div>
          <div>
            <Text strong style={{ color: textColor }}>Status: </Text>
            <Tag color={status === 'completed' ? 'success' : 'warning'}>{output.status}</Tag>
          </div>
          <div>
            <Text strong style={{ color: textColor }}>E-Signature: </Text>
            <Text style={{ color: textColor }}>{output.electronicSignature}</Text>
          </div>
          <div>
            <Text strong style={{ color: textColor }}>Audit Trail: </Text>
            <Text style={{ color: textColor }}>{output.auditTrail}</Text>
          </div>
        </Space>
      </Card>
    );
  };

  const renderTriggers = (triggers) => {
    return (
      <div className="triggers-section">
        <Title level={5} style={{ color: '#fa8c16', marginBottom: 12 }}>
          <PlayCircleOutlined /> Trigger Conditions for Next Stage
        </Title>
        <List
          dataSource={triggers}
          renderItem={(trigger) => (
            <List.Item style={{ padding: '8px 0', paddingLeft: 16 }}>
              <Space>
                <CheckOutlined style={{ color: '#52c41a' }} />
                <Text style={{ color: secondaryTextColor }}>{trigger}</Text>
              </Space>
            </List.Item>
          )}
        />
      </div>
    );
  };

  const renderCompliance = (compliance) => {
    return (
      <Card
        size="small"
        style={{
          marginTop: 16,
          backgroundColor: isDark ? defaultColors.palette.gray[600] : '#e6f7ff',
          borderColor: '#1890ff'
        }}
      >
        <Title level={5} style={{ color: isDark ? '#69c0ff' : '#0050b3', marginBottom: 12 }}>
          <LockOutlined /> 21 CFR Part 11 Compliance
        </Title>
        <List
          dataSource={compliance}
          renderItem={(item) => (
            <List.Item style={{ padding: '8px 0', paddingLeft: 16 }}>
              <Space>
                <CheckCircleOutlined style={{ color: '#1890ff' }} />
                <Text style={{ color: isDark ? '#69c0ff' : '#0050b3' }}>{item}</Text>
              </Space>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  const timelineItems = workflow.map((stage) => {
    const status = getStageStatus(stage.stage);

    return {
      color: status === 'completed' ? 'green' : status === 'active' ? 'blue' : 'gray',
      dot: getStatusDot(status),
      children: (
        <Card
          className={`workflow-stage-card ${status === 'active' ? 'active-stage' : ''}`}
          style={{
            marginBottom: 24,
            backgroundColor: cardBgColor,
            borderColor: status === 'active' ? '#1890ff' : borderColor,
            boxShadow: status === 'active' ? '0 4px 12px rgba(24, 144, 255, 0.3)' : undefined,
            border: status === 'active' ? '2px solid #1890ff' : `1px solid ${borderColor}`
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Header */}
            <div>
              <Row justify="space-between" align="middle">
                <Col>
                  <Title level={4} style={{ margin: 0, color: status === 'active' ? '#1890ff' : textColor }}>
                    Stage {stage.stage}: {stage.stageName}
                  </Title>
                </Col>
                <Col>
                  <Space>
                    <Tag color={getStatusColor(status)} style={{ fontSize: '14px', fontWeight: 600 }}>
                      {stage.stageCode}
                    </Tag>
                  </Space>
                </Col>
              </Row>
              <Paragraph style={{ marginTop: 8, marginBottom: 0, color: secondaryTextColor }}>
                {stage.description}
              </Paragraph>
            </div>

            <Divider style={{ margin: 0, borderColor: borderColor }} />

            {/* Collapsible Details */}
            <Collapse
              defaultActiveKey={status === 'active' ? ['1'] : []}
              ghost
              expandIconPosition="end"
            >
              <Panel header={<Text strong style={{ color: textColor }}>View Stage Details</Text>} key="1">
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  {/* Key Activities */}
                  {renderKeyActivities(stage.keyActivities)}

                  <Divider style={{ borderColor: borderColor }} />

                  {/* Stakeholders */}
                  {renderStakeholders(stage.stakeholders)}

                  <Divider style={{ borderColor: borderColor }} />

                  {/* Output */}
                  {renderOutput(stage.output, status)}

                  {/* Trigger Conditions */}
                  {stage.triggerForNext && stage.triggerForNext.length > 0 && (
                    <>
                      <Divider style={{ borderColor: borderColor }} />
                      {renderTriggers(stage.triggerForNext)}
                    </>
                  )}

                  {/* 21 CFR Compliance */}
                  <Divider style={{ borderColor: borderColor }} />
                  {renderCompliance(stage.cfr21Compliance)}
                </Space>
              </Panel>
            </Collapse>

            {/* Action Buttons */}
            {status === 'active' && (
              <div style={{ marginTop: 16 }}>
                <Space>
                  <Button
                    type="default"
                    onClick={() => onStageAction && onStageAction('view_details', stage)}
                  >
                    View Details
                  </Button>
                  <Button
                    type="primary"
                    icon={<SafetyOutlined />}
                    onClick={() => onApprovalClick && onApprovalClick(stage)}
                  >
                    Approve & Sign
                  </Button>
                </Space>
              </div>
            )}

            {/* Status Indicators */}
            {status === 'completed' && (
              <Alert
                message="Stage Completed Successfully"
                description={
                  workflowData && workflowData[`stage${stage.stage}_data`]
                    ? `Approved by: ${workflowData[`stage${stage.stage}_data`]?.approver || 'System'} on ${workflowData[`stage${stage.stage}_data`]?.date || 'N/A'}`
                    : undefined
                }
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
              />
            )}

            {status === 'pending' && (
              <Alert
                message="Awaiting Completion of Previous Stages"
                type="info"
                showIcon
                icon={<WarningOutlined />}
              />
            )}
          </Space>
        </Card>
      )
    };
  });

  return (
    <div className={`workflow-timeline-antd-container ${isDark ? 'dark-mode' : 'light-mode'}`}>
      <Timeline mode="left" items={timelineItems} />
    </div>
  );
};

export default WorkflowTimelineAntd;
